import React, { useRef, useState, useLayoutEffect } from "react";
import { PhoneMockup } from "@/pages/LessonPage";
import { Crosshair, Minus, Plus } from "lucide-react";

const SCALE = 1.4;

export default function FeaturePositionPicker({
  features = [],
  activeIndex = 0,
  image,
  onSelect,
  onPositionChange,
}) {
  const boxRef = useRef(null);
  const outerRef = useRef(null);
  const [size, setSize] = useState(null);
  const dragging = useRef(false);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      if (r.width && r.height) {
        setSize({ w: Math.round(r.width * SCALE), h: Math.round(r.height * SCALE) });
      }
    };
    measure();
    const img = el.querySelector("img");
    if (img) {
      if (img.complete) {
        measure();
      } else {
        img.addEventListener("load", measure);
        return () => img.removeEventListener("load", measure);
      }
    }
  }, [image]);

  const clampPct = (n) => Math.round(Math.min(100, Math.max(0, n)));

  const handlePointerDown = (e) => {
    if (features.length === 0) return;
    const rect = outerRef.current.getBoundingClientRect();
    const x = clampPct(((e.clientX - rect.left) / rect.width) * 100);
    const y = clampPct(((e.clientY - rect.top) / rect.height) * 100);
    const hotspotEl = e.target.closest ? e.target.closest("[data-hotspot]") : null;
    const idx = hotspotEl ? Number(hotspotEl.dataset.hotspot) : activeIndex;
    const target = features[idx] ? idx : 0;
    if (!features[target]) return;
    onSelect(target);
    if (!hotspotEl) onPositionChange(target, x, y);
    dragging.current = { index: target };
    outerRef.current.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!dragging.current) return;
    const rect = outerRef.current.getBoundingClientRect();
    const x = clampPct(((e.clientX - rect.left) / rect.width) * 100);
    const y = clampPct(((e.clientY - rect.top) / rect.height) * 100);
    onPositionChange(dragging.current.index, x, y);
  };

  const endDrag = () => {
    dragging.current = false;
  };

  const activeFeature = features[activeIndex];
  const nudge = (axis, delta) => {
    if (!activeFeature) return;
    const pos = activeFeature.position || { x: 50, y: 50 };
    const x = axis === "x" ? clampPct(pos.x + delta) : pos.x;
    const y = axis === "y" ? clampPct(pos.y + delta) : pos.y;
    onPositionChange(activeIndex, x, y);
  };

  const nudgeBtn = (axis, delta, label) => (
    <button
      type="button"
      aria-label={label}
      className="w-7 h-7 flex items-center justify-center"
      style={{
        background: "#FDF5EB",
        borderRadius: "50%",
        border: "1.5px solid #242220",
        color: "#242220",
        cursor: "pointer",
      }}
      onClick={() => nudge(axis, delta)}
    >
      {delta > 0 ? <Plus size={12} strokeWidth={2.5} /> : <Minus size={12} strokeWidth={2.5} />}
    </button>
  );

  return (
    <div
      className="p-4 mb-4"
      style={{
        background: "#fff",
        borderRadius: "16px",
        border: "1.5px solid #242220",
        boxShadow: "2px 2px 0 #242220",
      }}
      data-testid="feature-position-picker"
    >
      <div className="flex items-center gap-2 mb-1">
        <Crosshair size={16} strokeWidth={2.5} color="#242220" />
        <p className="text-sm font-bold" style={{ color: "#242220" }}>
          Vizuális pozíció beállító
        </p>
      </div>
      <p className="text-xs mb-3" style={{ color: "#242220", opacity: 0.55 }}>
        Kattints a képre a kijelölt funkció pozíciójának beállításához, vagy húzd a számozott pontot a finom pozicionáláshoz. A pontok ugyanúgy jelennek meg a telefon mockupon, mint a tanulók számára.
      </p>

      <div
        ref={outerRef}
        className="relative mx-auto"
        style={{
          width: size ? size.w : 320,
          height: size ? size.h : 590,
          cursor: "crosshair",
          touchAction: "none",
          userSelect: "none",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            transform: `scale(${size ? SCALE : 1})`,
            transformOrigin: "top left",
            pointerEvents: "none",
          }}
        >
          <div ref={boxRef} className="relative">
            <PhoneMockup image={image} />
            {features.map((feature, index) => (
              <button
                key={feature.id || index}
                type="button"
                data-hotspot={index}
                data-testid={`hotspot-btn-${index}`}
                className="absolute transition-all duration-200"
                style={{
                  left: `${feature.position?.x ?? 50}%`,
                  top: `${feature.position?.y ?? 50}%`,
                  transform: "translate(-50%, -50%)",
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  border: "2.5px solid #242220",
                  background:
                    index === activeIndex
                      ? "rgba(251, 213, 86, 0.85)"
                      : "rgba(255, 255, 255, 0.65)",
                  boxShadow: "0 3px 12px rgba(36, 34, 32, 0.3)",
                  cursor: "grab",
                  pointerEvents: "auto",
                  zIndex: index === activeIndex ? 30 : 20,
                }}
              >
                <span
                  className="flex items-center justify-center w-full h-full text-xs font-bold"
                  style={{ color: "#242220" }}
                >
                  {index + 1}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {activeFeature && (
        <div
          className="mt-3 flex items-center justify-center gap-2 flex-wrap"
          style={{ color: "#242220" }}
        >
          <span className="text-xs font-bold">
            Kijelölt funkció: #{activeIndex + 1}
            {activeFeature.title ? ` – ${activeFeature.title}` : ""}
          </span>
          <span className="text-xs font-bold flex items-center gap-1" style={{ opacity: 0.7 }}>
            X: {activeFeature.position?.x ?? 50}%
            {nudgeBtn("x", -1, "X csökkentése")}
            {nudgeBtn("x", 1, "X növelése")}
          </span>
          <span className="text-xs font-bold flex items-center gap-1" style={{ opacity: 0.7 }}>
            Y: {activeFeature.position?.y ?? 50}%
            {nudgeBtn("y", -1, "Y csökkentése")}
            {nudgeBtn("y", 1, "Y növelése")}
          </span>
        </div>
      )}
    </div>
  );
}
